/**
 * Modal para registrar la decisión del Hiring Manager
 * Permite aprobar o rechazar al candidato con comentarios
 */

import { CapaDeModal } from '@/shared/components/CapaDeModal'
import React, { useState } from 'react'
import { 
  X,
  CheckCircle,
  XCircle,
  AlertCircle,
  MessageSquare,
  Loader2,
  ArrowRight,
  Upload,
  FileText,
  Trash2
} from 'lucide-react'
import type { 
  HiringManagerInterview,
  HiringManagerInterviewDecision,
  HiringManagerInterviewDecisionErrors 
} from '@/types/hiringManagerInterview'
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'

interface HiringManagerInterviewDecisionModalProps {
  interview: HiringManagerInterview | null
  isOpen: boolean
  onClose: () => void
  onSubmit: (decision: HiringManagerInterviewDecision, file?: File) => Promise<void>
}

export const HiringManagerInterviewDecisionModal: React.FC<HiringManagerInterviewDecisionModalProps> = ({
  interview,
  isOpen,
  onClose,
  onSubmit
}) => {
  const [decision, setDecision] = useState<'aprobado' | 'rechazado' | null>(null)
  const [comentarios, setComentarios] = useState('')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [errors, setErrors] = useState<HiringManagerInterviewDecisionErrors>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [showConfirmation, setShowConfirmation] = useState(false)

  const handleDecisionChange = (newDecision: 'aprobado' | 'rechazado') => {
    setDecision(newDecision)
    setErrors({})
    setShowConfirmation(false)
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      // Validar tamaño (10MB máximo)
      const maxSize = 10 * 1024 * 1024
      if (file.size > maxSize) {
        setErrors(prev => ({ 
          ...prev, 
          archivoResultados: 'El archivo no debe superar los 10MB' 
        }))
        return
      }

      // Validar tipo de archivo
      const allowedTypes = ['application/pdf', 'application/msword', 
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'image/jpeg', 'image/png']
      
      if (!allowedTypes.includes(file.type)) {
        setErrors(prev => ({ 
          ...prev, 
          archivoResultados: 'Solo se permiten archivos PDF, Word o imágenes' 
        }))
        return
      }

      setSelectedFile(file)
      setErrors(prev => ({ ...prev, archivoResultados: undefined }))
    }
  }

  const handleRemoveFile = () => {
    setSelectedFile(null)
    setErrors(prev => ({ ...prev, archivoResultados: undefined }))
  }

  const validateForm = (): boolean => {
    const newErrors: HiringManagerInterviewDecisionErrors = {}

    if (!decision) {
      newErrors.decision = 'Debes seleccionar una decisión'
    }

    // Los comentarios son opcionales pero recomendados
    if (!comentarios.trim() && decision === 'rechazado') {
      // Advertencia para rechazos sin comentarios
      newErrors.comentarios = 'Se recomienda agregar comentarios al rechazar un candidato'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm() || !decision) {
      return
    }

    // Mostrar confirmación antes de enviar
    if (!showConfirmation) {
      setShowConfirmation(true)
      return
    }

    setIsSubmitting(true)
    setSubmitError(null)

    try {
      await onSubmit({
        decision,
        comentarios: comentarios.trim() || undefined
      }, selectedFile || undefined)

      // Limpiar y cerrar
      handleClose()
    } catch (errorCrudo: unknown) {
      const error = errorCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error('Error recording decision:', error)
      setSubmitError(error.message || 'Error al registrar la decisión')
      setShowConfirmation(false)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    setDecision(null)
    setComentarios('')
    setSelectedFile(null)
    setErrors({})
    setSubmitError(null)
    setShowConfirmation(false)
    onClose()
  }

  if (!isOpen || !interview) return null

  return (
    <CapaDeModal onClickFondo={!isSubmitting ? handleClose : undefined}>
        <div className="w-full max-w-2xl overflow-hidden text-left bg-white shadow-xl rounded-lg">
          {/* Header */}
          <div className={`px-6 py-4 ${
            decision === 'aprobado' ? 'bg-gradient-to-r from-green-600 to-green-700' :
            decision === 'rechazado' ? 'bg-gradient-to-r from-red-600 to-red-700' :
            'bg-gradient-to-r from-blue-600 to-blue-700'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-white/20 p-2 rounded-lg">
                  {decision === 'aprobado' ? (
                    <CheckCircle className="w-6 h-6 text-white" />
                  ) : decision === 'rechazado' ? (
                    <XCircle className="w-6 h-6 text-white" />
                  ) : (
                    <AlertCircle className="w-6 h-6 text-white" />
                  )}
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">
                    Registrar Decisión
                  </h2>
                  <p className="text-blue-100 text-sm">
                    {interview.candidate.fullName}
                  </p>
                </div>
              </div>
              <button
                onClick={handleClose}
                disabled={isSubmitting}
                className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors disabled:opacity-50"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <form onSubmit={handleSubmit} className="px-6 py-6">
            {/* Error general */}
            {submitError && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-red-900">Error</h4>
                  <p className="text-sm text-red-700">{submitError}</p>
                </div>
              </div>
            )}

            {/* Información del candidato */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-2">Candidato</h3>
              <p className="text-sm text-gray-700">
                <span className="font-medium">Nombre:</span> {interview.candidate.fullName}
              </p>
              <p className="text-sm text-gray-700">
                <span className="font-medium">Email:</span> {interview.candidate.email}
              </p>
              <p className="text-sm text-gray-700">
                <span className="font-medium">Teléfono:</span> {interview.candidate.phone}
              </p>
            </div>

            {/* Selección de decisión */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Decisión * 
                <span className="text-gray-500 font-normal ml-2">(Esta decisión es obligatoria)</span>
              </label>
              <div className="grid grid-cols-2 gap-4">
                {/* Opción Aprobar */}
                <button
                  type="button"
                  onClick={() => handleDecisionChange('aprobado')}
                  disabled={isSubmitting}
                  className={`p-6 border-2 rounded-lg transition-all ${
                    decision === 'aprobado'
                      ? 'border-green-600 bg-green-50'
                      : 'border-gray-300 hover:border-gray-400'
                  } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <CheckCircle className={`w-12 h-12 mx-auto mb-3 ${
                    decision === 'aprobado' ? 'text-green-600' : 'text-gray-400'
                  }`} />
                  <h4 className={`font-semibold mb-2 ${
                    decision === 'aprobado' ? 'text-green-900' : 'text-gray-700'
                  }`}>
                    Aprobar Candidato
                  </h4>
                  <p className="text-sm text-gray-600">
                    El candidato pasa a estado <strong>SELECCIONADO</strong> y se habilita para contratación
                  </p>
                </button>

                {/* Opción Rechazar */}
                <button
                  type="button"
                  onClick={() => handleDecisionChange('rechazado')}
                  disabled={isSubmitting}
                  className={`p-6 border-2 rounded-lg transition-all ${
                    decision === 'rechazado'
                      ? 'border-red-600 bg-red-50'
                      : 'border-gray-300 hover:border-gray-400'
                  } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <XCircle className={`w-12 h-12 mx-auto mb-3 ${
                    decision === 'rechazado' ? 'text-red-600' : 'text-gray-400'
                  }`} />
                  <h4 className={`font-semibold mb-2 ${
                    decision === 'rechazado' ? 'text-red-900' : 'text-gray-700'
                  }`}>
                    Rechazar Candidato
                  </h4>
                  <p className="text-sm text-gray-600">
                    El candidato regresa a <strong>ENTREVISTA_RH</strong> para que se proponga otro candidato
                  </p>
                </button>
              </div>
              {errors.decision && (
                <p className="mt-2 text-sm text-red-600">{errors.decision}</p>
              )}
            </div>

            {/* Comentarios */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <MessageSquare className="inline-block w-4 h-4 mr-1" />
                Comentarios
                {decision === 'rechazado' && (
                  <span className="text-red-600 ml-1">*</span>
                )}
                <span className="text-gray-500 font-normal ml-2">
                  {decision === 'rechazado' 
                    ? '(Requerido para rechazos)' 
                    : '(Opcional pero recomendado)'}
                </span>
              </label>
              <textarea
                value={comentarios}
                onChange={(e) => {
                  setComentarios(e.target.value)
                  if (errors.comentarios) {
                    setErrors(prev => ({ ...prev, comentarios: undefined }))
                  }
                }}
                disabled={isSubmitting}
                rows={4}
                placeholder={
                  decision === 'aprobado'
                    ? 'Ej: El candidato cumple con todos los requisitos técnicos y de liderazgo. Excelente perfil para el puesto...'
                    : decision === 'rechazado'
                    ? 'Ej: No cuenta con experiencia suficiente en gestión de equipos grandes. Requiere al menos 5 años de experiencia...'
                    : 'Agrega comentarios sobre tu decisión...'
                }
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.comentarios ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.comentarios && (
                <p className="mt-1 text-sm text-red-600">{errors.comentarios}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                {comentarios.length} caracteres
              </p>
            </div>

            {/* Archivo de Resultados (Opcional) */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Upload className="inline-block w-4 h-4 mr-1" />
                Archivo de Resultados
                <span className="text-gray-500 font-normal ml-2">(Opcional)</span>
              </label>
              <p className="text-sm text-gray-600 mb-3">
                Puedes adjuntar un archivo con los resultados de la entrevista (evaluación, notas, etc.)
              </p>
              
              {!selectedFile ? (
                <div className="relative">
                  <input
                    type="file"
                    onChange={handleFileChange}
                    disabled={isSubmitting}
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    className="hidden"
                    id="results-file-upload"
                  />
                  <label
                    htmlFor="results-file-upload"
                    className={`flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                      isSubmitting
                        ? 'border-gray-300 bg-gray-50 cursor-not-allowed'
                        : 'border-gray-300 hover:border-blue-500 hover:bg-blue-50'
                    }`}
                  >
                    <Upload className="w-5 h-5 text-gray-400" />
                    <span className="text-sm text-gray-600">
                      Click para seleccionar archivo
                    </span>
                  </label>
                  <p className="mt-1 text-xs text-gray-500">
                    Formatos permitidos: PDF, Word, Imágenes (JPG, PNG). Máximo 10MB
                  </p>
                </div>
              ) : (
                <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <FileText className="w-8 h-8 text-blue-600 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {selectedFile.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleRemoveFile}
                    disabled={isSubmitting}
                    className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors disabled:opacity-50"
                    title="Eliminar archivo"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              )}
              
              {errors.archivoResultados && (
                <p className="mt-1 text-sm text-red-600">{errors.archivoResultados}</p>
              )}
            </div>

            {/* Confirmación */}
            {showConfirmation && decision && (
              <div className={`mb-6 p-4 rounded-lg border-2 ${
                decision === 'aprobado'
                  ? 'bg-green-50 border-green-300'
                  : 'bg-red-50 border-red-300'
              }`}>
                <div className="flex items-start gap-3">
                  <AlertCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                    decision === 'aprobado' ? 'text-green-600' : 'text-red-600'
                  }`} />
                  <div>
                    <h4 className={`font-semibold mb-2 ${
                      decision === 'aprobado' ? 'text-green-900' : 'text-red-900'
                    }`}>
                      ¿Confirmas tu decisión?
                    </h4>
                    <p className={`text-sm mb-3 ${
                      decision === 'aprobado' ? 'text-green-800' : 'text-red-800'
                    }`}>
                      {decision === 'aprobado' ? (
                        <>
                          Al aprobar, el candidato <strong>{interview.candidate.fullName}</strong>{' '}
                          pasará al estado <strong>SELECCIONADO</strong> y estará listo para el proceso de contratación.
                        </>
                      ) : (
                        <>
                          Al rechazar, el candidato <strong>{interview.candidate.fullName}</strong>{' '}
                          regresará al estado <strong>ENTREVISTA_RH</strong> para que Recursos Humanos proponga otro candidato.
                        </>
                      )}
                    </p>
                    <div className={`flex items-center gap-2 text-sm ${
                      decision === 'aprobado' ? 'text-green-700' : 'text-red-700'
                    }`}>
                      <span>Estado actual: <strong>{interview.candidate.status}</strong></span>
                      <ArrowRight className="w-4 h-4" />
                      <span>Nuevo estado: <strong>{decision === 'aprobado' ? 'seleccionado' : 'entrevista_rh'}</strong></span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={handleClose}
                disabled={isSubmitting}
                className={estiloDeBoton('secundario')}
              >
                Cancelar
              </button>
              
              {!showConfirmation ? (
                <button
                  type="submit"
                  disabled={!decision || isSubmitting}
                  className={estiloDeBoton(decision === 'aprobado' ? 'exito' : decision === 'rechazado' ? 'peligro' : 'primario')}
                >
                  Continuar
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={estiloDeBoton(decision === 'aprobado' ? 'exito' : 'peligro')}
                >
                  {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  {isSubmitting ? 'Registrando...' : 'Confirmar Decisión'}
                </button>
              )}
            </div>
          </form>
        </div>
    </CapaDeModal>
  )
}
